import { useEffect, useMemo, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { UserPlus, Eye, Send, Plus, Trash2, ShieldCheck } from "lucide-react";

// Variant catalogue — each entry knows how to assemble its `spec` from the flat form state.
const VARIANTS = [
  { key: "individual", label: "Individual", tax: "01", blurb: "Resident, single holder, physical" },
  { key: "demat", label: "Demat", tax: "01", blurb: "Resident individual, demat mode" },
  { key: "minor", label: "Minor", tax: "02", blurb: "On behalf of minor + guardian" },
  { key: "nri", label: "NRI", tax: "21/24", blurb: "NRE / NRO with foreign address" },
  { key: "joint", label: "Joint", tax: "01", blurb: "2–3 holders, JO / AS" },
  { key: "non_individual", label: "Non-Individual", tax: "05/55", blurb: "AOP / FPI / QFI with UBO" },
];

const F = (v) => (v == null ? "" : v);

// --- small field helpers -------------------------------------------------
function Field({ label, hint, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-[var(--ink-muted)]">{label}</Label>
      <Input {...props} className="h-9" />
      {hint && <p className="text-[10px] text-[var(--ink-muted)]">{hint}</p>}
    </div>
  );
}

function Group({ title, children, right }) {
  return (
    <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--surface-2)]/40 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{title}</h3>
        {right}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

const emptyHolder = () => ({ first_name: "", last_name: "", dob: "", gender: "M", pan: "", occ_code: "01" });
const emptyUbo = () => ({ first_name: "", last_name: "", dob: "", pan: "", beneficiary_percent: "" });

// --- spec builders per variant (mirror bse_v2.ucc_builder signatures) -----
function buildSpec(variant, s) {
  const contact = { email: s.email, phone: s.phone };
  const bank = { ifsc: s.bank_ifsc, acc_num: s.bank_acc, acc_type: s.bank_type || undefined };
  const comm = { line1: s.addr1, city: s.city, state: s.state, pincode: s.pincode };
  const foreign = { line1: s.f_line1, city: s.f_city, state: s.f_state, country: s.f_country, pincode: s.f_pincode };
  if (variant === "individual" || variant === "demat") {
    const kyc = {
      pan: s.pan, dob: s.dob, first_name: s.first_name, gender: s.gender,
      city: s.city, state: s.state, pincode: s.pincode, bank_ifsc: s.bank_ifsc,
      bank_account_no: s.bank_acc, occupation_code: s.occ_code || "01",
    };
    if (variant === "demat") Object.assign(kyc, { depository_code: s.dp_code || "CDSL", dp_id: s.dp_id, dp_client_id: s.dp_client_id });
    return { ...contact, full_name: `${s.first_name} ${s.last_name}`.trim(), kyc };
  }
  if (variant === "minor") {
    return {
      ...contact, bank, comm,
      minor: { first_name: s.first_name, last_name: s.last_name, dob: s.dob, gender: s.gender, pan: s.pan || "" },
      guardian: { first_name: s.g_first, last_name: s.g_last, dob: s.g_dob, gender: s.g_gender || "M",
        pan: s.g_pan || "", pan_exempt_ref_no: s.g_pan ? "" : s.g_exempt_ref, guardian_relation: s.g_relation || "6" },
    };
  }
  if (variant === "nri") {
    return { ...contact, kind: s.nri_kind || "NRE", bank, comm, foreign,
      holder: { first_name: s.first_name, last_name: s.last_name, dob: s.dob, gender: s.gender, pan: s.pan, occ_code: s.occ_code || "01" } };
  }
  if (variant === "joint") {
    return { ...contact, bank, comm, holding_nature: s.holding_nature || "JO", holders: s.holders || [] };
  }
  if (variant === "non_individual") {
    return {
      ...contact, tax_code: s.tax_code || "05", entity_name: s.entity_name, pan: s.pan, bank,
      comm: s.f_country ? undefined : comm, foreign: s.f_country ? foreign : undefined,
      incorporation_date: s.incorporation_date, occ_code: s.occ_code || "03",
      ubo_details: (s.ubos || []).map((u) => ({
        first_name: u.first_name, last_name: u.last_name, dob: u.dob, pan: u.pan,
        beneficiary_percent: Number(u.beneficiary_percent) || 0, ubo_type_code: "C01", ubo_category: "CP",
      })),
    };
  }
  return {};
}

export default function UccOnboarding() {
  const [variant, setVariant] = useState("individual");
  const [s, setS] = useState({ gender: "M", bank_type: "SB", holding_nature: "JO", nri_kind: "NRE" });
  const [holders, setHolders] = useState([emptyHolder(), emptyHolder()]);
  const [ubos, setUbos] = useState([emptyUbo()]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState(null);
  const [ops, setOps] = useState([]);
  const [maintBusy, setMaintBusy] = useState(false);
  const [maint, setMaint] = useState({ client_code: "", field: "contact", email: "", phone: "", ifsc: "", acc_num: "", acc_type: "SB", dp_code: "CDSL", dp_id: "", dp_client_id: "", nominee_name: "", nominee_dob: "", nominee_pan: "", nominee_relation: "10" });

  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    adminApi.get("/admin/bse2/health").then((r) => setHealth(r.data)).catch(() => {});
    adminApi.get("/admin/bse2/ucc/ops").then((r) => setOps(r.data.ops || [])).catch(() => {});
  }, []);

  const refreshOps = () => adminApi.get("/admin/bse2/ucc/ops").then((r) => setOps(r.data.ops || []));
  const setMaintField = (key) => (event) => setMaint((prev) => ({ ...prev, [key]: event.target.value }));
  const maintenanceSpec = () => {
    if (maint.field === "contact") return { contact: { email: maint.email, phone: maint.phone, contact_type: "PR" } };
    if (maint.field === "bank") return { add: [{ ifsc: maint.ifsc.toUpperCase(), acc_num: maint.acc_num, acc_type: maint.acc_type, account_owner: "SELF" }] };
    if (maint.field === "depository") return { add: [{ code: maint.dp_code, dp_id: maint.dp_id, client_id: maint.dp_client_id, bank_account: maint.acc_num, account_owner: "SELF" }] };
    return { nominees: [{ name: maint.nominee_name, dob: maint.nominee_dob, pan: maint.nominee_pan.toUpperCase(), percent: 100, relation: maint.nominee_relation }], nominee_soa: true };
  };
  const updateUcc = async (dryRun) => {
    if (!maint.client_code) return toast.error("Enter the BSE client code to update");
    setMaintBusy(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/update", { client_code: maint.client_code.toUpperCase(), field: maint.field, spec: maintenanceSpec(), dry_run: dryRun });
      if (dryRun) { setPreview(data.payload); toast.success("Update payload built for review"); }
      else if (data.status === "SUBMITTED") { toast.success(data.message || "UCC update submitted to BSE"); await refreshOps(); }
      else toast.error(data.message || `BSE did not accept the UCC update (${data.status || "unknown status"})`);
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setMaintBusy(false); }
  };
  const deactivateUcc = async () => {
    if (!maint.client_code) return toast.error("Enter the BSE client code to deactivate");
    if (!window.confirm(`Deactivate UCC ${maint.client_code.toUpperCase()} at BSE? This blocks new transactions.`)) return;
    setMaintBusy(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/deactivate", { client_code: maint.client_code.toUpperCase() });
      data.status === "SUBMITTED" ? toast.success(data.message) : toast.error(data.message || `BSE returned ${data.status}`);
      await refreshOps();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setMaintBusy(false); }
  };

  const clientCode = s.client_code || "";
  const fullSpec = useMemo(() => {
    const merged = { ...s, holders: holders, ubos: ubos };
    return buildSpec(variant, merged);
  }, [variant, s, holders, ubos]);

  const call = async (dry) => {
    if (!clientCode) { toast.error("Client code is required"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/create-variant", {
        client_code: clientCode, variant, spec: fullSpec, dry_run: dry,
      });
      if (dry) { setPreview(data.payload); toast.success("Payload built — review the wire preview"); }
      else {
        setPreview(null);
        data.status === "SUBMITTED"
          ? toast.success(`UCC ${clientCode} submitted to BSE (${variant})`)
          : toast.error(data.message || "BSE rejected the registration");
      }
    } catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setBusy(false); }
  };

  const cur = VARIANTS.find((v) => v.key === variant);

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]" data-testid="ucc-onboarding-title">UCC Onboarding</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">
            Register any investor profile on BSE StAR MF. Build the exact wire payload, preview it, then submit.
          </p>
        </div>
        {health && (
          <HealthPill health={health} />
        )}
      </div>

      {/* Variant picker — a real taxonomy, not decorative steps */}
      <div className="mt-6 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {VARIANTS.map((v) => {
          const active = v.key === variant;
          return (
            <button
              key={v.key}
              onClick={() => { setVariant(v.key); setPreview(null); }}
              data-testid={`variant-${v.key}`}
              className={`rounded-xl border p-3 text-left transition ${
                active
                  ? "border-[var(--cta)] bg-[var(--cta)]/10 shadow-sm"
                  : "border-[var(--glass-border)] hover:border-[var(--cta)]/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{v.label}</span>
                <span className="text-[10px] text-[var(--ink-muted)]">tax {v.tax}</span>
              </div>
              <p className="mt-1 text-[11px] leading-tight text-[var(--ink-muted)]">{v.blurb}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        {/* form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserPlus className="h-4 w-4 text-[var(--cta)]" /> {cur.label} registration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Group title="Identity">
              <Field label="Client code (UCC)" value={F(s.client_code)} onChange={set("client_code")} placeholder="e.g. SMIFS100234" />
              {variant === "non_individual"
                ? <Field label="Entity name" value={F(s.entity_name)} onChange={set("entity_name")} />
                : <Field label="First name" value={F(s.first_name)} onChange={set("first_name")} />}
              {variant !== "non_individual" && <Field label="Last name" value={F(s.last_name)} onChange={set("last_name")} />}
              <Field label="PAN" value={F(s.pan)} onChange={set("pan")} placeholder="ABCPE1234F" />
              {variant === "non_individual"
                ? <Field label="Incorporation date" type="date" value={F(s.incorporation_date)} onChange={set("incorporation_date")} />
                : <Field label="Date of birth" type="date" value={F(s.dob)} onChange={set("dob")} />}
              {variant === "non_individual" && <Field label="Tax code" value={F(s.tax_code)} onChange={set("tax_code")} placeholder="05 AOP · 55 FPI" />}
            </Group>

            <Group title="Contact">
              <Field label="Email" type="email" value={F(s.email)} onChange={set("email")} />
              <Field label="Mobile" value={F(s.phone)} onChange={set("phone")} placeholder="9830000000" />
            </Group>

            {variant === "nri" && (
              <Group title="NRI type">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[var(--ink-muted)]">Account type</Label>
                  <div className="flex gap-2">
                    {["NRE", "NRO"].map((k) => (
                      <button key={k} onClick={() => setS((p) => ({ ...p, nri_kind: k }))}
                        className={`flex-1 rounded-lg border px-3 py-2 text-sm ${s.nri_kind === k ? "border-[var(--cta)] bg-[var(--cta)]/10 font-semibold" : "border-[var(--glass-border)]"}`}>
                        {k} <span className="text-[10px] text-[var(--ink-muted)]">tax {k === "NRE" ? "21" : "24"}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </Group>
            )}

            {variant === "minor" && (
              <Group title="Guardian">
                <Field label="Guardian first name" value={F(s.g_first)} onChange={set("g_first")} />
                <Field label="Guardian last name" value={F(s.g_last)} onChange={set("g_last")} />
                <Field label="Guardian DOB" type="date" value={F(s.g_dob)} onChange={set("g_dob")} />
                <Field label="Guardian PAN" value={F(s.g_pan)} onChange={set("g_pan")} hint="Leave blank if PAN-exempt" />
                <Field label="PAN-exempt ref no" value={F(s.g_exempt_ref)} onChange={set("g_exempt_ref")} hint="Used only when no PAN" />
                <Field label="Relation code" value={F(s.g_relation)} onChange={set("g_relation")} placeholder="6 = grandparent" />
              </Group>
            )}

            {variant === "joint" && (
              <Group title="Holders" right={
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                  disabled={holders.length >= 3}
                  onClick={() => setHolders((h) => [...h, emptyHolder()])}><Plus className="h-3 w-3" /> Add</Button>
              }>
                <div className="sm:col-span-2 space-y-3">
                  {holders.map((h, i) => (
                    <div key={i} className="rounded-lg border border-[var(--glass-border)] p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold">Holder {i + 1}{i === 0 ? " (primary)" : ""}</span>
                        {holders.length > 2 && (
                          <button onClick={() => setHolders((hs) => hs.filter((_, j) => j !== i))}
                            className="text-[var(--ink-muted)] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Input placeholder="First name" className="h-9" value={h.first_name}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))} />
                        <Input placeholder="Last name" className="h-9" value={h.last_name}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))} />
                        <Input placeholder="PAN" className="h-9" value={h.pan}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, pan: e.target.value } : x))} />
                        <Input placeholder="DOB (YYYY-MM-DD)" className="h-9" value={h.dob}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, dob: e.target.value } : x))} />
                      </div>
                    </div>
                  ))}
                </div>
              </Group>
            )}

            {variant === "non_individual" && (
              <Group title="Ultimate beneficial owners" right={
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                  onClick={() => setUbos((u) => [...u, emptyUbo()])}><Plus className="h-3 w-3" /> Add UBO</Button>
              }>
                <div className="sm:col-span-2 space-y-3">
                  {ubos.map((u, i) => (
                    <div key={i} className="rounded-lg border border-[var(--glass-border)] p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold">UBO {i + 1}</span>
                        {ubos.length > 1 && (
                          <button onClick={() => setUbos((us) => us.filter((_, j) => j !== i))}
                            className="text-[var(--ink-muted)] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Input placeholder="First name" className="h-9" value={u.first_name}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))} />
                        <Input placeholder="PAN" className="h-9" value={u.pan}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, pan: e.target.value } : x))} />
                        <Input placeholder="DOB" className="h-9" value={u.dob}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, dob: e.target.value } : x))} />
                        <Input placeholder="Beneficiary %" className="h-9" value={u.beneficiary_percent}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, beneficiary_percent: e.target.value } : x))} />
                      </div>
                    </div>
                  ))}
                </div>
              </Group>
            )}

            <Group title="Bank account">
              <Field label="IFSC" value={F(s.bank_ifsc)} onChange={set("bank_ifsc")} placeholder="HDFC0000060" />
              <Field label="Account number" value={F(s.bank_acc)} onChange={set("bank_acc")} />
              <Field label="Account type" value={F(s.bank_type)} onChange={set("bank_type")}
                hint={variant === "nri" ? "Auto NE/NO for NRI if left blank" : "SB / CB"} />
            </Group>

            {variant === "demat" && (
              <Group title="Depository">
                <Field label="Depository" value={F(s.dp_code)} onChange={set("dp_code")} placeholder="CDSL / NSDL" />
                <Field label="DP ID" value={F(s.dp_id)} onChange={set("dp_id")} />
                <Field label="Client ID" value={F(s.dp_client_id)} onChange={set("dp_client_id")} />
              </Group>
            )}

            {variant === "nri" || (variant === "non_individual" && s.f_country) ? (
              <Group title="Foreign address">
                <Field label="Address line" value={F(s.f_line1)} onChange={set("f_line1")} />
                <Field label="City" value={F(s.f_city)} onChange={set("f_city")} />
                <Field label="State / region" value={F(s.f_state)} onChange={set("f_state")} />
                <Field label="Country code" value={F(s.f_country)} onChange={set("f_country")} placeholder="USA / IND (ISO-3)" />
                <Field label="Postal code" value={F(s.f_pincode)} onChange={set("f_pincode")} />
              </Group>
            ) : (
              <Group title="Communication address">
                <Field label="Address line" value={F(s.addr1)} onChange={set("addr1")} />
                <Field label="City" value={F(s.city)} onChange={set("city")} />
                <Field label="State" value={F(s.state)} onChange={set("state")} />
                <Field label="Pincode" value={F(s.pincode)} onChange={set("pincode")} />
                {variant === "non_individual" && (
                  <Field label="Foreign country (if non-resident)" value={F(s.f_country)} onChange={set("f_country")}
                    hint="Set to switch to a foreign address for FPI/QFI" />
                )}
              </Group>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Button onClick={() => call(true)} disabled={busy} variant="outline" className="rounded-full" data-testid="ucc-preview-button">
                <Eye className="mr-2 h-4 w-4" /> Preview wire payload
              </Button>
              <Button onClick={() => call(false)} disabled={busy} className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="ucc-submit-button">
                <Send className="mr-2 h-4 w-4" /> {busy ? "Working…" : "Submit to BSE"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* signature: live wire-payload preview */}
        <Card className="lg:sticky lg:top-6 self-start">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-[var(--cta)]" /> Wire payload
              <Badge variant="outline" className="ml-auto font-mono text-[10px]">/v2/add_ucc</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <pre className="max-h-[70vh] overflow-auto rounded-lg bg-slate-950 p-4 text-[11px] leading-relaxed text-emerald-200"
                data-testid="ucc-preview-json">
                {JSON.stringify(preview, null, 2)}
              </pre>
            ) : (
              <div className="rounded-lg border border-dashed border-[var(--glass-border)] p-8 text-center">
                <p className="text-sm text-[var(--ink-muted)]">
                  Fill the form and choose <span className="font-medium text-[var(--ink)]">Preview wire payload</span> to
                  see the exact JSON that will be sent to BSE — verify it before submitting.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Maintain an existing BSE UCC</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[var(--ink-muted)]">Every action below calls BSE&apos;s real update_ucc API and is recorded in the operations log.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Client code" value={maint.client_code} onChange={setMaintField("client_code")} placeholder="BSE UCC" />
            <div className="space-y-1.5"><Label className="text-xs text-[var(--ink-muted)]">Update type</Label>
              <select className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm" value={maint.field} onChange={setMaintField("field")}>
                <option value="contact">Primary contact</option><option value="bank">Add bank account</option><option value="depository">Add demat account</option><option value="nominee">Nomination</option>
              </select>
            </div>
            {maint.field === "contact" && <><Field label="Email" type="email" value={maint.email} onChange={setMaintField("email")} /><Field label="Mobile" value={maint.phone} onChange={setMaintField("phone")} /></>}
            {maint.field === "bank" && <><Field label="IFSC" value={maint.ifsc} onChange={setMaintField("ifsc")} /><Field label="Account number" value={maint.acc_num} onChange={setMaintField("acc_num")} /></>}
            {maint.field === "depository" && <><div className="space-y-1.5"><Label className="text-xs text-[var(--ink-muted)]">Depository</Label><select className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm" value={maint.dp_code} onChange={setMaintField("dp_code")}><option value="CDSL">CDSL</option><option value="NSDL">NSDL</option></select></div><Field label="DP ID" value={maint.dp_id} onChange={setMaintField("dp_id")} /><Field label="Client ID" value={maint.dp_client_id} onChange={setMaintField("dp_client_id")} /><Field label="Linked bank account" value={maint.acc_num} onChange={setMaintField("acc_num")} /></>}
            {maint.field === "nominee" && <><Field label="Nominee name" value={maint.nominee_name} onChange={setMaintField("nominee_name")} /><Field label="Date of birth" type="date" value={maint.nominee_dob} onChange={setMaintField("nominee_dob")} /><Field label="PAN (optional)" value={maint.nominee_pan} onChange={setMaintField("nominee_pan")} /></>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" disabled={maintBusy} onClick={() => updateUcc(true)}><Eye className="mr-2 h-4 w-4" /> Preview update</Button>
            <Button className="rounded-full bg-[var(--cta)]" disabled={maintBusy} onClick={() => updateUcc(false)}><Send className="mr-2 h-4 w-4" /> Submit update to BSE</Button>
            <Button variant="outline" className="rounded-full text-red-700" disabled={maintBusy} onClick={deactivateUcc}>Deactivate UCC</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Recent UCC exchange operations</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {ops.length === 0 ? <p className="text-sm text-[var(--ink-muted)]">No UCC operations recorded.</p> : ops.slice(0, 20).map((op, index) => (
            <div key={`${op.at}-${index}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--glass-border)] p-3 text-xs">
              <span><b>{op.client_code}</b> · {op.op}</span>
              <span>{op.result?.status || op.status || "UNKNOWN"} · {op.result?.message || ""} · {op.at?.replace("T", " ").slice(0, 19)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

function HealthPill({ health }) {
  const ok = health.ok;
  return (
    <div className="flex items-center gap-2 rounded-full border border-[var(--glass-border)] px-3 py-1.5"
      data-testid="ucc-health-pill" title={ok ? "All live submissions reconciled" : health.alerts.join(" · ")}>
      <ShieldCheck className={`h-4 w-4 ${ok ? "text-emerald-500" : "text-amber-500"}`} />
      <span className="text-xs font-medium">{ok ? "Money-safety: healthy" : `${health.alerts.length} alert(s)`}</span>
      {health.failed_last_24h > 0 && <Badge className="bg-amber-500/90 text-[10px]">{health.failed_last_24h} failed 24h</Badge>}
    </div>
  );
}
